import { motion } from "framer-motion";
import {
    ArrowRight,
    Archive,
    BookmarkPlus,
    CheckCircle2,
    MapPin,
    PhoneOff,
    Trash2,
    UserMinus,
    UserX,
    X,
} from "lucide-react";

/**
 * Custom call-action modal — replaces the ugly native `window.confirm`.
 *
 * Outcomes presented as cards, grouped from POSITIVE to NEGATIVE:
 *
 *   ✅ "Programmer l'immersion"     → opens the schedule-immersion modal
 *   ⏳ "Pas de réponse"              → /no-answer, candidate moves to À rappeler
 *   🟠 "Candidat plus disponible"    → /reject with reason=candidat_indisponible (soft archive)
 *   🔴 "Refus définitif"             → /reject with reason=criteres_non_remplis (hard refus)
 *   📞 "Numéro injoignable"          → /reject with reason=numero_injoignable (only shown after 3+ attempts)
 *   ↩  "Reprendre l'appel"           → close modal
 *
 * Props:
 *   - candidate (object)
 *   - callAttempts (number)
 *   - onSchedule ()           → success path: schedule immersion
 *   - onNoAnswer ()
 *   - onArchiveUnavailable () → reject with reason=candidat_indisponible
 *   - onRejectDefinitive ()   → reject with reason=criteres_non_remplis
 *   - onUnreachable ()        → reject with reason=numero_injoignable (only shown after 3+ attempts)
 *   - onClose ()
 */
export default function CallActionModal({
    candidate,
    callAttempts,
    onSchedule,
    onNoAnswer,
    onArchiveUnavailable,
    onRejectDefinitive,
    onUnreachable,
    onTalentPool,
    onClose,
}) {
    const nextAttempt = (callAttempts || 0) + 1;
    const recallText = nextAttempt === 1
        ? "Marquer le candidat comme « à rappeler »."
        : `Marquer une ${nextAttempt}ᵉ tentative sans réponse.`;
    const showUnreachable = (callAttempts || 0) >= 2; // suggest rejection after 3+ attempts
    // Recommend "talent pool" when the candidate looks promising (good AI
    // score) but we keep failing to reach them — saves the lead for later.
    const aiScore = candidate?.ai_score || 0;
    const recommendPool = aiScore >= 70 && (callAttempts || 0) >= 2;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 bg-shariti-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            data-testid="call-action-modal"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl max-w-lg w-full my-6 max-h-[88vh] flex flex-col"
            >
                <div className="flex items-center gap-3 p-5 md:p-6 border-b border-shariti-gray shrink-0">
                    {candidate.avatar_url ? (
                        <img src={candidate.avatar_url} alt="" className="h-11 w-11 rounded-2xl object-cover shrink-0" />
                    ) : (
                        <div className="h-11 w-11 rounded-2xl bg-shariti-orange-soft text-shariti-orange font-extrabold text-base flex items-center justify-center shrink-0">
                            {candidate.first_name?.[0]}
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            Qualifier l'appel
                        </div>
                        <div className="text-base font-extrabold text-shariti-black truncate">
                            {candidate.first_name} {candidate.last_name}
                        </div>
                        <div className="text-[11px] font-medium text-slate-500">
                            {callAttempts > 0 ? `Déjà ${callAttempts} tentative${callAttempts > 1 ? "s" : ""} sans réponse` : "Aucune tentative précédente"}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="call-action-close"
                        className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream text-slate-500 hover:text-shariti-red hover:bg-red-50 transition shrink-0"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="px-5 md:px-6 py-4 space-y-2 overflow-y-auto flex-1">
                    {/* ────────── POSITIVE — Schedule immersion ────────── */}
                    {onSchedule && (
                        <div>
                            <div className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-600 mb-1 px-1">
                                Entretien validé
                            </div>
                            <button
                                onClick={onSchedule}
                                data-testid="action-schedule-immersion"
                                className="group w-full text-left flex items-start gap-3 p-3 rounded-2xl border-2 border-emerald-300 bg-emerald-50/70 hover:bg-emerald-50 hover:border-emerald-400 transition"
                            >
                                <div className="h-9 w-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-emerald-500/30">
                                    <MapPin className="h-4 w-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13px] font-extrabold text-shariti-black flex items-center gap-1.5">
                                        Programmer l'entretien immersif
                                        <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500 text-white">
                                            Recommandé
                                        </span>
                                    </div>
                                    <div className="mt-0.5 text-[11px] font-medium leading-snug text-slate-600">
                                        Le candidat correspond. Choisissez date, lieu et référent — le SMS récap suit.
                                    </div>
                                </div>
                                <ArrowRight className="h-4 w-4 text-emerald-700 mt-1.5 opacity-0 group-hover:opacity-100 transition" />
                            </button>
                        </div>
                    )}

                    {/* ────────── INCONCLUSIVE — No answer ────────── */}
                    <div className={onSchedule ? "pt-1.5" : ""}>
                        <div className="text-[10px] font-extrabold uppercase tracking-widest text-amber-700 mb-1 px-1">
                            En attente
                        </div>
                        <button
                            onClick={onNoAnswer}
                            data-testid="action-no-answer"
                            className="group w-full text-left flex items-start gap-3 p-3 rounded-2xl border-2 border-amber-200 bg-amber-50/60 hover:bg-amber-50 hover:border-amber-300 transition"
                        >
                            <div className="h-9 w-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                                <PhoneOff className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-extrabold text-shariti-black flex items-center gap-1.5">
                                    Pas de réponse
                                    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                                        À rappeler
                                    </span>
                                </div>
                                <div className="mt-0.5 text-[11px] font-medium leading-snug text-slate-600">
                                    {recallText} Reste dans la file, visible dans « À rappeler ».
                                </div>
                            </div>
                            <ArrowRight className="h-4 w-4 text-amber-700 mt-1.5 opacity-0 group-hover:opacity-100 transition" />
                        </button>
                    </div>

                    {/* ────────── SOFT NEGATIVE — Candidate not available ────────── */}
                    {(onArchiveUnavailable || onRejectDefinitive || onTalentPool || showUnreachable) && (
                        <div className="pt-1.5">
                            <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1 px-1">
                                Sortie de la file
                            </div>
                            <div className="space-y-2">
                                {onArchiveUnavailable && (
                                    <button
                                        onClick={onArchiveUnavailable}
                                        data-testid="action-archive-unavailable"
                                        className="group w-full text-left flex items-start gap-3 p-3 rounded-2xl border-2 border-slate-200 bg-slate-50/70 hover:bg-slate-50 hover:border-slate-300 transition"
                                    >
                                        <div className="h-9 w-9 rounded-xl bg-slate-200 text-slate-700 flex items-center justify-center shrink-0">
                                            <UserMinus className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-extrabold text-shariti-black">
                                                Candidat plus disponible
                                            </div>
                                            <div className="mt-0.5 text-[11px] font-medium leading-snug text-slate-600">
                                                Trouvé autre chose / plus motivé. <strong>Archivé</strong> proprement.
                                            </div>
                                        </div>
                                        <ArrowRight className="h-4 w-4 text-slate-500 mt-1.5 opacity-0 group-hover:opacity-100 transition" />
                                    </button>
                                )}

                                {onRejectDefinitive && (
                                    <button
                                        onClick={onRejectDefinitive}
                                        data-testid="action-reject-definitive"
                                        className="group w-full text-left flex items-start gap-3 p-3 rounded-2xl border-2 border-red-200 bg-red-50/40 hover:bg-red-50 hover:border-red-300 transition"
                                    >
                                        <div className="h-9 w-9 rounded-xl bg-red-100 text-shariti-red flex items-center justify-center shrink-0">
                                            <UserX className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-extrabold text-shariti-black">
                                                Refus définitif
                                            </div>
                                            <div className="mt-0.5 text-[11px] font-medium leading-snug text-slate-600">
                                                Le candidat <strong>ne correspond pas</strong> aux critères. Sort de la file.
                                            </div>
                                        </div>
                                        <ArrowRight className="h-4 w-4 text-shariti-red mt-1.5 opacity-0 group-hover:opacity-100 transition" />
                                    </button>
                                )}

                                {/* Talent pool option — softer than refusing, encouraged when score is good but unreachable */}
                                {onTalentPool && (
                                    <button
                                        onClick={onTalentPool}
                                        data-testid="action-talent-pool"
                                        className={`group w-full text-left flex items-start gap-3 p-3 rounded-2xl border-2 transition ${
                                            recommendPool
                                                ? "border-amber-300 bg-amber-50/60 hover:bg-amber-50 hover:border-amber-400"
                                                : "border-slate-200 bg-slate-50/40 hover:bg-amber-50/60 hover:border-amber-200"
                                        }`}
                                    >
                                        <div className="h-9 w-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                                            <BookmarkPlus className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-extrabold text-shariti-black flex items-center gap-1.5 flex-wrap">
                                                Mettre au vivier
                                                {recommendPool && (
                                                    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 border border-amber-300">
                                                        Recommandé · score {aiScore}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-0.5 text-[11px] font-medium leading-snug text-slate-600">
                                                Bon profil mais <strong>pas joignable / pas dispo / no-show</strong>. Reste accessible depuis <em>Vivier</em>.
                                            </div>
                                        </div>
                                        <ArrowRight className="h-4 w-4 text-amber-700 mt-1.5 opacity-0 group-hover:opacity-100 transition" />
                                    </button>
                                )}

                                {/* Only show when 3+ failed attempts */}
                                {showUnreachable && onUnreachable && (
                                    <button
                                        onClick={onUnreachable}
                                        data-testid="action-unreachable"
                                        className="group w-full text-left flex items-start gap-3 p-3 rounded-2xl border-2 border-red-200 bg-red-50/40 hover:bg-red-50 hover:border-red-300 transition"
                                    >
                                        <div className="h-9 w-9 rounded-xl bg-red-100 text-shariti-red flex items-center justify-center shrink-0">
                                            <Trash2 className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-extrabold text-shariti-black flex items-center gap-1.5">
                                                Numéro injoignable
                                                <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-shariti-orange-soft text-shariti-orange border border-shariti-orange/30">
                                                    Recommandé
                                                </span>
                                            </div>
                                            <div className="mt-0.5 text-[11px] font-medium leading-snug text-slate-600">
                                                Numéro faux / hors service. Refusé définitivement.
                                            </div>
                                        </div>
                                        <ArrowRight className="h-4 w-4 text-shariti-red mt-1.5 opacity-0 group-hover:opacity-100 transition" />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ────────── Resume ────────── */}
                </div>

                {/* Sticky footer with "Reprendre l'appel" */}
                <div className="border-t border-shariti-gray p-3 md:p-4 bg-shariti-cream/40 shrink-0">
                    <button
                        onClick={onClose}
                        data-testid="action-cancel"
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-xs font-extrabold uppercase tracking-wider text-slate-600 bg-white hover:bg-shariti-cream border border-shariti-gray hover:border-shariti-orange/40 transition"
                    >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Reprendre l'appel
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}
